import type { ActiveTimer, Mode, Settings } from '@timato/shared';
import { uuidv7 } from '@timato/shared';

/**
 * Pure timer engine (architecture §5.2). The row stores banked time (`accumSec`) plus the start of the
 * current running stretch (`anchorAt`). Every transition is a small write; the display is derived from the clock.
 * No React, no Dexie, no Date.now() - callers pass `now` so this is trivially testable and device-agnostic.
 */

export const IDLE_TIMER: ActiveTimer = {
  id: 'active', runId: null, status: 'idle', mode: 'focus', taskId: null, projectId: null, goalText: '', labelIds: null,
  startedAt: null, durationSec: null, accumSec: 0, anchorAt: null, idleSegments: [], cycleCount: 0,
  startedOnDeviceId: null, updatedAt: '', deletedAt: null,
};

export const MODE_LABEL: Record<Mode, string> = { focus: 'Focus', short: 'Short break', long: 'Long break', track: 'Track' };
export const isBreak = (mode: Mode) => mode === 'short' || mode === 'long';
export const isCountdown = (mode: Mode) => mode !== 'track';

export function durationFor(mode: Mode, settings: Settings): number | null {
  switch (mode) {
    case 'focus': return settings.focusMin * 60;
    case 'short': return settings.shortMin * 60;
    case 'long': return settings.longMin * 60;
    case 'track': return null;
  }
}

/** Seconds elapsed in this run at `now`. */
export function elapsedSec(t: ActiveTimer, now: Date): number {
  const running = t.status === 'running' && t.anchorAt ? (now.getTime() - Date.parse(t.anchorAt)) / 1000 : 0;
  return Math.max(0, t.accumSec + running);
}

/** Seconds left on a countdown (may be negative once overdue); null in track mode. */
export function remainingSec(t: ActiveTimer, now: Date): number | null {
  return t.durationSec == null ? null : t.durationSec - elapsedSec(t, now);
}

export function isComplete(t: ActiveTimer, now: Date): boolean {
  const r = remainingSec(t, now);
  return t.status === 'running' && r != null && r <= 0;
}

/** Wall-clock moment a running countdown reaches zero; used to timestamp completions after a sleep or reload. */
export function completesAt(t: ActiveTimer): Date | null {
  if (t.status !== 'running' || !t.anchorAt || t.durationSec == null) return null;
  return new Date(Date.parse(t.anchorAt) + (t.durationSec - t.accumSec) * 1000);
}

/** Fraction of the countdown consumed, 0..1. Track mode: fraction of the current minute (drives the marker). */
export function progress(t: ActiveTimer, now: Date): number {
  const e = elapsedSec(t, now);
  if (t.durationSec == null) return (e % 60) / 60;
  return Math.min(1, Math.max(0, e / t.durationSec));
}

type Ctx = { now: Date; settings: Settings; deviceId: string };

export function start(t: ActiveTimer, { now, settings, deviceId }: Ctx, goal?: Pick<ActiveTimer, 'taskId' | 'projectId' | 'goalText'>): ActiveTimer {
  return {
    ...(goal ? setGoal(t, goal) : t),
    runId: uuidv7(now.getTime()), status: 'running',
    startedAt: now.toISOString(), durationSec: durationFor(t.mode, settings),
    accumSec: 0, anchorAt: now.toISOString(), idleSegments: [], startedOnDeviceId: deviceId,
  };
}

export function pause(t: ActiveTimer, now: Date): ActiveTimer {
  if (t.status !== 'running') return t;
  return { ...t, status: 'paused', accumSec: elapsedSec(t, now), anchorAt: null };
}

export function resume(t: ActiveTimer, now: Date): ActiveTimer {
  if (t.status !== 'paused') return t;
  return { ...t, status: 'running', anchorAt: now.toISOString() };
}

/** Back to idle in the same mode, nothing logged. `cycleCount` and goal are kept. */
export function reset(t: ActiveTimer): ActiveTimer {
  return { ...t, runId: null, status: 'idle', startedAt: null, durationSec: null, accumSec: 0, anchorAt: null, idleSegments: [], startedOnDeviceId: null };
}

/** The same row pointed at another goal. Labels picked for this session belonged to the old one, so they go. */
export function setGoal(t: ActiveTimer, goal: Pick<ActiveTimer, 'taskId' | 'projectId' | 'goalText'>): ActiveTimer {
  return { ...t, ...goal, labelIds: null };
}

/** Idle again after a session was logged: the labels picked for that session are spent (TW-6). */
export function afterSession(t: ActiveTimer): ActiveTimer {
  return { ...reset(t), labelIds: null };
}

/** Switch mode while idle (or abandon the current run). */
export function setMode(t: ActiveTimer, mode: Mode): ActiveTimer {
  return { ...reset(t), mode };
}

/** The mode that follows a completed one, per the pomodoro cycle. Track always returns to track. */
export function nextMode(t: ActiveTimer, settings: Settings): { mode: Mode; cycleCount: number } {
  if (t.mode === 'track') return { mode: 'track', cycleCount: t.cycleCount };
  if (t.mode === 'focus') {
    const cycleCount = t.cycleCount + 1;
    return { mode: cycleCount % settings.longAfter === 0 ? 'long' : 'short', cycleCount };
  }
  // after a break: back to focus; a long break closes the cycle
  return { mode: 'focus', cycleCount: t.mode === 'long' ? 0 : t.cycleCount };
}

/** Human label for the counter line: `Session 2 of 4 · next Short break · 5m`. */
export function cycleLabel(t: ActiveTimer, settings: Settings): { session: string; next: string } {
  const position = t.mode === 'focus' ? Math.min(t.cycleCount + 1, settings.longAfter) : Math.max(1, Math.min(t.cycleCount, settings.longAfter));
  const n = nextMode(t, settings);
  const dur = durationFor(n.mode, settings);
  return { session: `Session ${position} of ${settings.longAfter}`, next: `next ${MODE_LABEL[n.mode]}${dur ? ` · ${Math.round(dur / 60)}m` : ''}` };
}

export function statusLabel(t: ActiveTimer, now: Date): string {
  if (t.status === 'idle') return t.mode === 'track' ? 'Counting up · no target' : 'Paused';
  if (t.status === 'paused') return 'Paused';
  if (isComplete(t, now)) return 'Session complete';
  return t.mode === 'track' ? 'Tracking' : isBreak(t.mode) ? 'On break' : 'Focusing';
}
